import {
  AUTHENTICATION_FLOW_STATE,
  AuthenticationFlowState
} from '~/src/stores/authentication-flow'
import { useEoState } from '~/src/hooks/useEoState'
import { useEffect, useMemo, useState } from 'react'
import useConfirmationCodeResend from '~/src/hooks/auth/useConfirmationCodeResend'
import { DateUtils } from '@eo-storefronts/eo-core'
import { intervalToDuration, Duration } from 'date-fns'
import { useTranslations } from '~/src/hooks/useTranslations'

interface ReturnsType {
  resend(): Promise<void>,
  resendIn: string | undefined,
}

const useAuthenticationFlowLoginTypeSelectionOnSubmit = (): ReturnsType => {
  const { t } = useTranslations()
  const [ authenticationFlowState, setAuthenticationFlowState ] = useEoState<AuthenticationFlowState>(AUTHENTICATION_FLOW_STATE)
  const { resend: resendConfirmationCode, errors, loading } = useConfirmationCodeResend()
  let interval: NodeJS.Timeout | undefined

  const calculateSecondsUntilTomorrow = (): Duration | undefined => {
    if (
      !authenticationFlowState.confirmationCodeSentAt ||
      DateUtils.diff(new Date(authenticationFlowState.confirmationCodeSentAt), new Date(), 'seconds') <= 0
    ) {
      return
    }

    return intervalToDuration({
      start: new Date(authenticationFlowState.confirmationCodeSentAt),
      end: new Date()
    })
  }

  const [ secondsRemaining, setSecondsRemaining ] = useState(calculateSecondsUntilTomorrow())
  
  const resendIn: string | undefined = useMemo<string | undefined>(() => {
    const sentenceParts: string[] = []

    if (secondsRemaining?.years) {
      sentenceParts.push(`${secondsRemaining?.years} ${t(`global.timeUnits.year${secondsRemaining.years > 1 ? 's' : ''}`)}`)
    }

    if (secondsRemaining?.months) {
      sentenceParts.push(`${secondsRemaining?.months} ${t(`global.timeUnits.month${secondsRemaining.months > 1 ? 's' : ''}`)}`)
    }

    if (secondsRemaining?.days) {
      sentenceParts.push(`${secondsRemaining?.days} ${t(`global.timeUnits.day${secondsRemaining.days > 1 ? 's' : ''}`)}`)
    }

    if (secondsRemaining?.hours) {
      sentenceParts.push(`${secondsRemaining?.hours} ${t(`global.timeUnits.hour${secondsRemaining.hours > 1 ? 's' : ''}`)}`)
    }

    if (secondsRemaining?.minutes) {
      sentenceParts.push(`${secondsRemaining?.minutes} ${t('global.timeUnits.minute')}`)
    }

    if (secondsRemaining?.seconds) {
      sentenceParts.push(`${secondsRemaining?.seconds} ${t(`global.timeUnits.second${secondsRemaining.seconds > 1 ? 's' : ''}`)}`)
    }

    return sentenceParts?.length ? sentenceParts.join(' ') : undefined
  }, [ secondsRemaining ])


  const resend = async (): Promise<void> => {
    interval && clearInterval(interval)
    setSecondsRemaining(calculateSecondsUntilTomorrow())

    const response: boolean = await resendConfirmationCode({
      email: authenticationFlowState.customer?.email || null,
      phone: authenticationFlowState.customer?.phone || null
    })

    if (response) {
      setAuthenticationFlowState((state: AuthenticationFlowState) => ({
        ...state,
        confirmationCodeSentAt: DateUtils.addMinutes(3, new Date()).toISOString()
      }))
    }
  }

  useEffect(() => {
    if (!authenticationFlowState.confirmationCodeSentAt) {
      return
    }

    interval && clearInterval(interval)

    interval = setInterval(() => {
      setSecondsRemaining(calculateSecondsUntilTomorrow())
    }, 1000)

    return () => {
      interval && clearInterval(interval)
    }
  }, [ authenticationFlowState.confirmationCodeSentAt ])

  useEffect(() => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      isLoading: loading
    }))
  }, [ loading ])

  useEffect(() => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      errors
    }))
  }, [ errors ])

  return {
    resend,
    resendIn
  }
}

export default useAuthenticationFlowLoginTypeSelectionOnSubmit
